import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';

import { SHARED_MODULES } from '../../../shared.';
import { PHPermissions } from '../../models/PHPermissions';
import { DateFormatService } from '../../services/date-format.service';
import { SessionService } from '../../services/session.service';
import { PhIconComponent } from '../ph-icon/ph-icon.component';

export interface IScheduledIconPlan {
	plan_id: number;
	plan_name: string;
	scheduled_dates: Array<string>;
}

@Component({
	selector: 'ph-scheduled-icon',
	templateUrl: './ph-scheduled-icon.component.html',
	styleUrls: ['./ph-scheduled-icon.component.scss'],
	imports: [SHARED_MODULES, PhIconComponent],
	standalone: true,
})
export class PhScheduledIconComponent {
	plans: InputSignal<Array<IScheduledIconPlan>> = input<Array<IScheduledIconPlan>>([]);
	withLink: InputSignal<boolean> = input(true);
	withTooltip: InputSignal<boolean> = input(true);

	private readonly translate: TranslateService = inject(TranslateService);
	private readonly dateFormatService: DateFormatService = inject(DateFormatService);
	private readonly sessionService: SessionService = inject(SessionService);
	private readonly router: Router = inject(Router);

	private readonly currentUrl: Signal<string> = toSignal(
		this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map((event) => event.urlAfterRedirects),
			startWith(this.router.url),
		),
		{ initialValue: this.router.url },
	);

	protected readonly link: Array<string | number> = this.sessionService.getLink(
		this.sessionService.hasAccess(PHPermissions.Control)
			? ['control', 'scheduled']
			: ['dashboard', 'scheduled']
	);

	protected readonly showLink: Signal<boolean> = computed(() => {
		if (!this.withLink()) return false;
		const url = this.currentUrl();
		return !url.includes('/control/scheduled') && !url.includes('/dashboard/scheduled');
	});

	protected readonly tooltip: Signal<string> = computed(() => this.buildPlansTooltip(this.plans()));

	private buildPlansTooltip(plans: Array<IScheduledIconPlan>): string {
		const dateLabel: string = this.translate.instant('SCHEDULED_PLANOGRAMS.DATE');

		return plans
			.map((plan: IScheduledIconPlan): string => {
				const dateLines: string = plan.scheduled_dates
					.map((date: string): string => `${dateLabel}: ${this.dateFormatService.formatDate(date, 'date')}`)
					.join('\n');

				return `${plan.plan_name}:\n${dateLines}`;
			})
			.join('\n\n');
	}
}
